import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import isEqual from 'lodash.isequal';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
  AccordionTabCloseEvent,
  AccordionTabOpenEvent,
} from 'primeng/accordion';
import { Card } from 'primeng/card';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TemplateDirective } from 'src/shared/directives/template.directive';

@Component({
  selector: 'tw-listbox',
  imports: [
    CommonModule,
    FormsModule,
    Card,
    InputText,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    IconField,
    InputIcon,
  ],
  templateUrl: './listbox.component.html',
  styleUrls: ['./listbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,

      useExisting: forwardRef(() => ListboxComponent),
      multi: true,
    },
  ],
})
export class ListboxComponent<T>
  implements ControlValueAccessor, OnChanges, AfterContentInit
{
  protected itemTemplate!: TemplateRef<any> | undefined;
  protected searchStr = '';
  protected expandedGroupState = new Map<number, boolean>();

  @Input() value!: T | T[keyof T];
  @Input() options: T[] = [];
  @Input() optionLabel = 'label';
  @Input() optionValue?: string;
  @Input() filter = false;
  @Input() groupSorting = false;
  @Input() allowGrouping = false;
  @Input() multiple = false;
  @Input() groupBy?: string;
  @Input() noGroupLabel = 'Other';
  @Input() orderByKey?: string;
  @Input() emptyFilterMessage = 'No records found';

  @Output() optionChangeEvent = new EventEmitter<T | T[keyof T]>();

  @ContentChildren(TemplateDirective)
  twTemplates?: QueryList<TemplateDirective>;

  get Groups() {
    return this.allowGrouping && this.groupBy
      ? this.groupSorting
        ? Object.keys(this.OptionsByGroup).sort((a, b) => (a > b ? 1 : -1))
        : Object.keys(this.OptionsByGroup)
      : [];
  }

  get Options() {
    return this.searchStr
      ? this.options.filter((opt) =>
          (opt[this.optionLabel as keyof T] as string)
            .toUpperCase()
            .includes(this.searchStr.toUpperCase()),
        )
      : this.options;
  }

  get OptionsByGroup() {
    const options = this.Options.reduce((acc: any, v) => {
      const key = v[this.groupBy as keyof T] ?? this.noGroupLabel;
      if (acc[key]) {
        acc[key].push(v);
      } else {
        acc[key] = [v];
      }
      return acc;
    }, {});

    return options;
  }

  get ShowEmptyFilterMessage() {
    return this.searchStr && !this.Options.length;
  }

  get TabActiveIndex() {
    const index = (
      this.groupSorting
        ? Object.keys(this.OptionsByGroup).sort((a, b) => (a > b ? 1 : -1))
        : Object.keys(this.OptionsByGroup)
    ).findIndex(
      (group) =>
        group ===
        (this.Options.find((opt) => this.isEqual(opt, this.value))?.[
          this.groupBy as keyof T
        ] ?? this.noGroupLabel),
    );

    // pre-populate when multiple accordion flag is set
    if (this.multiple) {
      // add current activeTab and also expand that group
      const indices = [index];
      this.expandedGroupState.set(index, true);

      for (const [key, value] of this.expandedGroupState.entries()) {
        if (value) {
          indices.push(key);
        }
      }
      return indices;
    }

    return index === -1 ? 0 : index;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      if (changes['orderByKey']) {
        const orderInvalid = (changes['options'].currentValue as T[])
          .filter((opt) => opt[this.orderByKey as keyof T])
          .map((opt) => opt[this.orderByKey as keyof T])
          .some((val) => isNaN(Number(val)));
        if (orderInvalid) {
          throw new Error(
            `Invalid Configuration - T[${changes['orderByKey'].currentValue}] accepts only numeric values!`,
          );
        }
      }

      this.options = (changes['options'].currentValue as T[])
        .map((o, index) => ({ ...o, index }))
        .sort((a, b) => {
          const aOrder = a[this.orderByKey as keyof T];
          const bOrder = b[this.orderByKey as keyof T];
          // If order key is present then compare between them else sort by initial options order
          return aOrder && bOrder
            ? aOrder > bOrder
              ? 1
              : -1
            : a.index > b.index
              ? 1
              : -1;
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ index, ...rest }) => rest) as T[];
    }
  }

  ngAfterContentInit() {
    this.twTemplates?.forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;
        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  isEqual(obj1: T | T[keyof T], obj2: T | T[keyof T]) {
    return isEqual(obj1, obj2);
  }

  onOptionClicked(option: T) {
    this.writeValue(option); // Update value depending on optionValue
    this.onChange(option); // Run change detection
    this.optionChangeEvent.emit(this.value); // Emit current value
  }

  searchFilter(event: Event) {
    const searchQuery = (event.target as HTMLInputElement).value || '';
    this.searchStr = searchQuery;
  }

  writeValue(value: any) {
    this.value =
      value && this.optionValue ? value[this.optionValue as keyof T] : value;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onChange(_: T | T[keyof T]) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched() {}

  accordionOpen(event: AccordionTabOpenEvent) {
    this.expandedGroupState.set(event.index, true);
  }

  accordionClose(event: AccordionTabCloseEvent) {
    this.expandedGroupState.set(event.index, false);
  }
}
